import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { images } from '../data/mock';

const Hero = () => {
  const { t } = useLanguage();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-amber-50 via-white to-amber-50/50">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${images.hero})` }}
        />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 text-center lg:text-start">
            <div className="inline-block">
              <div className="flex items-center gap-3 px-6 py-3 bg-amber-100 border border-amber-300 rounded-full">
                <div className="w-2 h-2 bg-amber-600 rounded-full animate-pulse" />
                <span className="text-amber-700 text-sm font-medium">
                  {t('language') === 'ar' ? 'مرحباً بكم' : 'Welcome'}
                </span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              <span className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 bg-clip-text text-transparent">
                {t('heroTitle').split(' ').slice(0, 2).join(' ')}
              </span>
              <br />
              <span className="text-slate-900">
                {t('heroTitle').split(' ').slice(2).join(' ')}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              {t('heroSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-amber-500/30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/40"
              >
                {t('heroButton')}
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-amber-600 bg-white hover:bg-amber-50 text-amber-700 font-semibold px-8 py-6 text-lg transition-all duration-300"
                onClick={() => window.open('tel:+971502060230')}
              >
                <Phone className="w-5 h-5 mr-2" />
                {t('language') === 'ar' ? 'اتصل الآن' : 'Call Now'}
              </Button>
            </div>

            {/* Quick contact info */}
            <div className="flex flex-col sm:flex-row gap-4 pt-8 justify-center lg:justify-start flex-wrap">
              <a href="tel:+971502060230" className="flex items-center gap-3 text-slate-600 hover:text-amber-600 transition-colors">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-slate-500">{t('language') === 'ar' ? 'اتصل بنا' : 'Call Us'}</div>
                  <div className="text-sm font-medium text-slate-900" dir="ltr">+971 50 206 0230</div>
                </div>
              </a>
              <a href="https://wa.me/971502060230" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-600 hover:text-green-600 transition-colors">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-slate-500">{t('language') === 'ar' ? 'واتساب' : 'WhatsApp'}</div>
                  <div className="text-sm font-medium text-slate-900" dir="ltr">+971 50 206 0230</div>
                </div>
              </a>
              <a href="mailto:fatimaabdelqudous@gmail.com" className="flex items-center gap-3 text-slate-600 hover:text-amber-600 transition-colors">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-slate-500">{t('language') === 'ar' ? 'راسلنا' : 'Email Us'}</div>
                  <div className="text-sm font-medium text-slate-900" dir="ltr">fatimaabdelqudous@gmail.com</div>
                </div>
              </a>
            </div>
          </div>

          {/* Image Section */}
          <div className="hidden lg:block relative">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-200/40 to-amber-300/40 rounded-2xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border-2 border-amber-200 shadow-2xl shadow-amber-500/20">
                <img 
                  src={images.office} 
                  alt="Law Office"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
              </div>
            </div>

            {/* Floating card */}
            <div className="absolute -bottom-8 -left-8 bg-white border-2 border-amber-200 rounded-xl p-6 shadow-2xl max-w-xs">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                  15+
                </div>
                <div>
                  <div className="text-slate-900 font-semibold text-lg">
                    {t('aboutYears')}
                  </div>
                  <div className="text-slate-600 text-sm">
                    {t('language') === 'ar' ? 'خبرة قانونية' : 'Legal Experience'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};

export default Hero;
